// @ts-nocheck
import React from 'react';
import { Link } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { bool } from 'prop-types';

import { Page, LayoutSingleColumn, SecondaryButtonInline } from '../components';
import TopbarContainer from '../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../containers/FooterContainer/FooterContainer';
import EndUserTradeTimeline from '../components/EndUserTradeTimeline/EndUserTradeTimeline';
import { getBxApp } from '../config/bxApps';
import { useActivityCart } from '../context/ActivityCartContext';
import { useJobBoard } from '../context/JobBoardContext';
import { isScrollingDisabled } from '../ducks/ui.duck';

import css from './ActivityHub.module.css';

const statusLabel = {
  draft: 'Draft',
  ready: 'Ready to launch',
  funded: 'Funded',
  in_progress: 'In progress',
  completed: 'Completed',
};

const ActivityHubComponent = ({ scrollingDisabled, isAuthenticated }) => {
  const { drafts, removeDraft, updateDraft } = useActivityCart();
  const { jobs, statusLabel: jobStatusLabel, removeJob } = useJobBoard();
  const activeDrafts = drafts.filter(d => d.status !== 'completed');
  const completedDrafts = drafts.filter(d => d.status === 'completed');
  const openJobs = jobs.filter(j => !['settled', 'cancelled'].includes(j.status));

  return (
    <Page title="Activity" scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        className={css.pageRoot}
      >
        <div className={css.container} data-testid="activity-hub-page">
          <header className={css.header}>
            <h1 className={css.title}>Activity & order book</h1>
            <p className={css.subtitle}>
              My requests, quotes, saved app flows, and live trade tracking — return anytime to
              complete or monitor status.
            </p>
          </header>

          <section className={css.section}>
            <h2 className={css.sectionTitle}>My requests & jobs</h2>
            {openJobs.length === 0 ? (
              <p className={css.empty}>
                No open requests yet. Post from{' '}
                <Link to="/apps/xcourier">Services</Link> or{' '}
                <Link to="/apps/xproxy">Payers & Delivery</Link>.
              </p>
            ) : (
              <ul className={css.list}>
                {openJobs.map(job => {
                  const app = getBxApp(job.appId);
                  return (
                    <li key={job.id} className={css.item}>
                      <div className={css.itemMain}>
                        <span
                          className={css.itemBadge}
                          style={{ '--app-accent': app?.accent || '#01bc8d' }}
                        >
                          {app?.shortName || job.appId}
                        </span>
                        <div>
                          <h3 className={css.itemTitle}>{job.title}</h3>
                          <p className={css.itemMeta}>
                            {jobStatusLabel(job.status)}
                            {job.providerName ? ` · ${job.providerName}` : ''}
                            {job.quotes?.length ? ` · ${job.quotes.length} quote(s)` : ''}
                          </p>
                        </div>
                      </div>
                      <div className={css.itemActions}>
                        <Link
                          to={`${app?.hubPath || '/landing'}?job=${job.id}`}
                          className={css.resumeLink}
                        >
                          Open
                        </Link>
                        <button
                          type="button"
                          className={css.removeBtn}
                          onClick={() => removeJob(job.id)}
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className={css.section}>
            <h2 className={css.sectionTitle}>Saved flows</h2>
            {activeDrafts.length === 0 ? (
              <p className={css.empty}>
                No saved flows yet. Open any app from the{' '}
                <Link to="/landing">Apps menu</Link> and complete a walkthrough.
              </p>
            ) : (
              <ul className={css.list}>
                {activeDrafts.map(draft => {
                  const app = getBxApp(draft.appId);
                  return (
                    <li key={draft.id} className={css.item}>
                      <div className={css.itemMain}>
                        <span
                          className={css.itemBadge}
                          style={{ '--app-accent': app?.accent || '#01bc8d' }}
                        >
                          {app?.shortName || draft.appId}
                        </span>
                        <div>
                          <h3 className={css.itemTitle}>{draft.title}</h3>
                          <p className={css.itemMeta}>
                            {statusLabel[draft.status] || draft.status} · Step{' '}
                            {draft.stepIndex + 1}
                            {app ? ` of ${app.steps.length}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className={css.itemActions}>
                        <Link
                          to={`${app?.hubPath || '/'}?draft=${draft.id}`}
                          className={css.resumeLink}
                        >
                          Resume
                        </Link>
                        {draft.status === 'ready' && draft.launchHref ? (
                          <Link to={draft.launchHref} className={css.launchLink}>
                            Launch
                          </Link>
                        ) : null}
                        <SecondaryButtonInline
                          type="button"
                          onClick={() => updateDraft(draft.id, { status: 'completed' })}
                        >
                          Done
                        </SecondaryButtonInline>
                        <button
                          type="button"
                          className={css.removeBtn}
                          onClick={() => removeDraft(draft.id)}
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className={css.section}>
            <EndUserTradeTimeline isAuthenticated={isAuthenticated} />
          </section>

          {completedDrafts.length > 0 ? (
            <section className={css.section}>
              <h2 className={css.sectionTitle}>Completed flows</h2>
              <ul className={css.listCompact}>
                {completedDrafts.slice(0, 8).map(draft => (
                  <li key={draft.id} className={css.compactItem}>
                    {draft.title}
                    <button
                      type="button"
                      className={css.removeBtn}
                      onClick={() => removeDraft(draft.id)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

ActivityHubComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  isAuthenticated: bool.isRequired,
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
  isAuthenticated: Boolean(state.auth?.isAuthenticated),
});

const ActivityHub = compose(connect(mapStateToProps))(ActivityHubComponent);

export default ActivityHub;
